import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function TermsOfService() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-900/80 backdrop-blur-md border-b border-slate-800/50">
        <div className="container mx-auto px-4 py-4">
          <button 
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft size={20} />
            Back to Home
          </button>
        </div>
      </header>

      {/* Content */}
      <div className="container mx-auto px-4 pt-24 pb-16">
        <Card className="max-w-4xl mx-auto bg-slate-800/50 border-slate-700/50 backdrop-blur-sm">
          <CardHeader className="text-center pb-8">
            <CardTitle className="text-4xl font-bold text-white mb-2">
              Terms of Service
            </CardTitle>
            <p className="text-slate-400">Last updated: January 2025</p>
          </CardHeader>

          <CardContent className="prose prose-invert max-w-none space-y-8 text-slate-300">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">1. Agreement to Terms</h2>
              <p className="mb-4">
                These Terms of Service ("Terms") govern your access to and use of the BirdenMedia website 
                at <span className="text-blue-400">birdenmedia.co</span> and any related services provided 
                by BirdenMedia ("we," "us," or "our").
              </p>
              <p>
                By accessing or using our website and services, you agree to be bound by these Terms. 
                If you disagree with any part of these Terms, you may not access our services.
              </p>
            </section>

            <Separator className="bg-slate-700/50" />

            {/* Services Description */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">2. Services Description</h2>
              <p className="mb-4">
                BirdenMedia provides digital marketing services including but not limited to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Google Ads campaign management</li>
                <li>Facebook and Instagram advertising</li>
                <li>Landing page design and development</li>
                <li>CRM setup and automation</li>
                <li>Marketing strategy consulting</li>
                <li>Performance reporting and analytics</li>
              </ul>
              <p className="mt-4">
                Specific services, deliverables, and pricing will be outlined in individual service 
                agreements or proposals provided to clients.
              </p>
            </section>

            <Separator className="bg-slate-700/50" />

            {/* Client Responsibilities */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">3. Client Responsibilities</h2>
              <p className="mb-4">As a client, you agree to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Provide accurate and complete information about your business</li>
                <li>Grant necessary access to advertising accounts and platforms</li>
                <li>Respond to requests for content, approvals, and feedback in a timely manner</li>
                <li>Pay for services according to agreed-upon payment terms</li>
                <li>Fund advertising accounts with required ad spend budgets</li>
                <li>Comply with advertising platform policies and guidelines</li>
                <li>Ensure all provided content complies with applicable laws</li>
              </ul>
            </section>

            <Separator className="bg-slate-700/50" />

            {/* Payment Terms */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">4. Payment Terms</h2>
              <p className="mb-4">
                Payment terms will be specified in your individual service agreement. General terms include:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong className="text-white">Management Fees:</strong> Billed monthly in advance</li>
                <li><strong className="text-white">Ad Spend:</strong> Funded directly to advertising platforms by the client</li>
                <li><strong className="text-white">Setup Fees:</strong> Due upon agreement signing, if applicable</li>
                <li><strong className="text-white">Late Payments:</strong> May result in service suspension after 15 days past due</li>
              </ul>
              <p className="mt-4">
                All fees are non-refundable unless otherwise specified in writing. Ad spend budgets 
                paid to third-party platforms are subject to those platforms' refund policies.
              </p>
            </section>

            <Separator className="bg-slate-700/50" />

            {/* Service Agreement & Cancellation */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">5. Service Term & Cancellation</h2>
              <p className="mb-4">
                Service terms and cancellation policies vary by plan:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li><strong className="text-white">Initial Commitment:</strong> Most plans require a 3-month minimum commitment</li>
                <li><strong className="text-white">Cancellation Notice:</strong> 30 days written notice required to cancel</li>
                <li><strong className="text-white">Early Termination:</strong> May be subject to early termination fees as specified in your agreement</li>
                <li><strong className="text-white">Month-to-Month:</strong> After initial commitment, services continue month-to-month</li>
              </ul>
              <p className="mt-4">
                Upon cancellation, we will provide you with access credentials and documentation 
                for any accounts or assets created on your behalf.
              </p>
            </section>

            <Separator className="bg-slate-700/50" />

            {/* Intellectual Property */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">6. Intellectual Property</h2>
              <h3 className="text-xl font-semibold text-white mb-3">6.1 Client Ownership</h3>
              <p className="mb-4">
                You retain ownership of all content, logos, trademarks, and materials you provide to us. 
                Upon full payment, you own all custom creative assets (landing pages, ad copy, graphics) 
                created specifically for your campaigns.
              </p>
              <h3 className="text-xl font-semibold text-white mb-3">6.2 BirdenMedia Ownership</h3>
              <p>
                We retain ownership of our proprietary tools, methodologies, templates, and processes. 
                We may use anonymized, aggregated data from client campaigns for internal analysis 
                and improvement of our services.
              </p>
            </section>

            <Separator className="bg-slate-700/50" />

            {/* Confidentiality */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">7. Confidentiality</h2>
              <p>
                Both parties agree to keep confidential any proprietary or sensitive information 
                shared during the course of the engagement. This includes business strategies, 
                campaign performance data, customer information, and pricing details. 
                Confidentiality obligations survive termination of services.
              </p>
            </section>

            <Separator className="bg-slate-700/50" />

            {/* Disclaimers & Limitations */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">8. Disclaimers & Limitations</h2>
              <h3 className="text-xl font-semibold text-white mb-3">8.1 No Guarantees</h3>
              <p className="mb-4">
                While we strive for excellent results, we cannot guarantee specific outcomes such as 
                lead volumes, conversion rates, or return on investment. Results depend on many factors 
                including market conditions, competition, and client responsiveness.
              </p>
              <h3 className="text-xl font-semibold text-white mb-3">8.2 Third-Party Platforms</h3>
              <p className="mb-4">
                We are not responsible for changes to third-party advertising platforms, their policies, 
                pricing, or availability. Platform account suspensions or policy violations by the client 
                are the client's responsibility.
              </p>
              <h3 className="text-xl font-semibold text-white mb-3">8.3 Limitation of Liability</h3>
              <p>
                To the maximum extent permitted by law, BirdenMedia's total liability for any claims 
                arising from these Terms or our services shall not exceed the total fees paid by you 
                in the three (3) months preceding the claim.
              </p>
            </section>

            <Separator className="bg-slate-700/50" />

            {/* Indemnification */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">9. Indemnification</h2>
              <p>
                You agree to indemnify and hold harmless BirdenMedia, its officers, employees, and 
                agents from any claims, damages, or expenses (including reasonable attorneys' fees) 
                arising from your breach of these Terms, your content, or your violation of any 
                third-party rights or applicable laws.
              </p>
            </section>

            <Separator className="bg-slate-700/50" />

            {/* Governing Law */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">10. Governing Law</h2>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the 
                State of North Carolina, United States, without regard to its conflict of law provisions. 
                Any disputes arising under these Terms shall be resolved in the courts of Wake County, 
                North Carolina.
              </p>
            </section>

            <Separator className="bg-slate-700/50" />

            {/* Changes to Terms */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">11. Changes to Terms</h2>
              <p>
                We reserve the right to modify these Terms at any time. We will notify existing clients 
                of material changes via email. Your continued use of our services after such modifications 
                constitutes acceptance of the updated Terms.
              </p>
            </section>

            <Separator className="bg-slate-700/50" />

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">12. Contact Us</h2>
              <p className="mb-4">
                If you have any questions about these Terms of Service, please contact us:
              </p>
              <div className="bg-slate-900/50 p-6 rounded-lg border border-slate-700/50">
                <p className="mb-2"><strong className="text-white">Email:</strong> legal@birdenmedia.co</p>
                <p><strong className="text-white">Website:</strong> birdenmedia.co</p>
              </div>
            </section>
          </CardContent>
        </Card>
      </div>

      {/* Footer */}
      <footer className="bg-slate-900/50 border-t border-slate-800/50 py-8 mt-16">
        <div className="container mx-auto px-4 text-center">
          <p className="text-slate-400">
            © {new Date().getFullYear()} BirdenMedia. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
